// File: frontend/src/utils/cronUtils.ts

/**
 * Helpers for the admin scheduler section.
 *
 * The backend uses Spring's 6-field cron format:
 *
 *   ┌───────────── second       (0-59)
 *   │ ┌─────────── minute       (0-59)
 *   │ │ ┌───────── hour         (0-23)
 *   │ │ │ ┌─────── day of month (1-31)
 *   │ │ │ │ ┌───── month        (1-12 or JAN-DEC)
 *   │ │ │ │ │ ┌─── day of week  (0-7 or MON-SUN, both 0 and 7 = Sunday)
 *   │ │ │ │ │ │
 *   0 0 3 * * *
 *
 * Note the leading SECONDS field — this is the part people coming from
 * classic 5-field Unix cron get wrong. "0 3 * * *" is not valid here;
 * the same schedule is "0 0 3 * * *".
 *
 * Nothing here validates as strictly as the server does: the backend is the
 * authority (it parses with CronExpression and rejects the save), this is only
 * enough to keep the panel from sending obvious nonsense and to turn a valid
 * expression into a sentence a human can read.
 */

// ── Presets ───────────────────────────────────────────────────────────────────

/** A ready-made schedule offered in the dropdown. */
export interface CronPreset {
  /** Stable id, also used as the <option> value. */
  id: string;
  /** The expression written into the input when selected. */
  cron: string;
  /** i18n key for the human-readable label. */
  labelKey: string;
}

/**
 * The dropdown contents, ordered from most to least frequent.
 *
 * These cover the schedules a maintenance job realistically needs. Anything
 * else is typed by hand through the "custom" option, which is why the raw cron
 * input is always visible next to the dropdown rather than hidden behind it.
 */
export const CRON_PRESETS: CronPreset[] = [
  { id: "every_minute",    cron: "0 * * * * *",    labelKey: "admin_scheduler_preset_every_minute" },
  { id: "every_5_minutes", cron: "0 */5 * * * *",  labelKey: "admin_scheduler_preset_every_5_minutes" },
  { id: "every_15_minutes",cron: "0 */15 * * * *", labelKey: "admin_scheduler_preset_every_15_minutes" },
  { id: "every_30_minutes",cron: "0 */30 * * * *", labelKey: "admin_scheduler_preset_every_30_minutes" },
  { id: "hourly",          cron: "0 0 * * * *",    labelKey: "admin_scheduler_preset_hourly" },
  { id: "hourly_at_5",     cron: "0 5 * * * *",    labelKey: "admin_scheduler_preset_hourly_at_5" },
  { id: "every_3_hours",   cron: "0 0 */3 * * *",  labelKey: "admin_scheduler_preset_every_3_hours" },
  { id: "every_6_hours",   cron: "0 0 */6 * * *",  labelKey: "admin_scheduler_preset_every_6_hours" },
  { id: "every_12_hours",  cron: "0 0 */12 * * *", labelKey: "admin_scheduler_preset_every_12_hours" },
  { id: "daily_3am",       cron: "0 0 3 * * *",    labelKey: "admin_scheduler_preset_daily_3am" },
  { id: "daily_midnight",  cron: "0 0 0 * * *",    labelKey: "admin_scheduler_preset_daily_midnight" },
  { id: "twice_daily",     cron: "0 0 3,15 * * *", labelKey: "admin_scheduler_preset_twice_daily" },
  { id: "weekly_monday",   cron: "0 0 3 * * MON",  labelKey: "admin_scheduler_preset_weekly_monday" },
  { id: "weekly_sunday",   cron: "0 0 4 * * SUN",  labelKey: "admin_scheduler_preset_weekly_sunday" },
  { id: "monthly_first",   cron: "0 0 3 1 * *",    labelKey: "admin_scheduler_preset_monthly_first" },
];

/** Value used by the dropdown when the expression matches no preset. */
export const CRON_CUSTOM_ID = "custom";

/** Which preset (if any) an expression corresponds to. Whitespace-insensitive. */
export const findPresetIdByCron = (cron: string): string => {
  const normalized = normalizeCron(cron);
  const match = CRON_PRESETS.find(p => normalizeCron(p.cron) === normalized);
  return match ? match.id : CRON_CUSTOM_ID;
};

/** Collapse runs of whitespace and upper-case names so two spellings compare equal. */
export const normalizeCron = (cron: string): string =>
  (cron || "").trim().replace(/\s+/g, " ").toUpperCase();

// ── Shallow validation ────────────────────────────────────────────────────────

/**
 * True when the expression has the right shape.
 *
 * Only checks the field count and the characters used — the backend does the
 * real parsing and returns a 400 with the offending job named. Catching the
 * 5-field mistake here saves a round trip for by far the most common typo.
 */
export const looksLikeValidCron = (cron: string): boolean => {
  const parts = normalizeCron(cron).split(" ").filter(Boolean);
  if (parts.length !== 6) return false;
  return parts.every(p => /^[0-9A-Z*/,\-?#L]+$/.test(p));
};

/** Number of whitespace-separated fields, used to explain a wrong-length expression. */
export const cronFieldCount = (cron: string): number =>
  normalizeCron(cron).split(" ").filter(Boolean).length;

// ── Human-readable description ────────────────────────────────────────────────

/**
 * Minimal shape of react-i18next's t(). Kept loose on purpose so the real
 * TFunction can be passed straight in without a cast.
 *
 * The interpolation variable is named `n`, never `count`: i18next treats a
 * `count` option as a plural request and starts looking for `_one` / `_other`
 * key variants that do not exist here.
 */
export type Translate = (key: string, options?: any) => string;

const DAY_KEYS: Record<string, string> = {
  "0": "admin_scheduler_day_sun",
  "1": "admin_scheduler_day_mon",
  "2": "admin_scheduler_day_tue",
  "3": "admin_scheduler_day_wed",
  "4": "admin_scheduler_day_thu",
  "5": "admin_scheduler_day_fri",
  "6": "admin_scheduler_day_sat",
  "7": "admin_scheduler_day_sun",
  SUN: "admin_scheduler_day_sun",
  MON: "admin_scheduler_day_mon",
  TUE: "admin_scheduler_day_tue",
  WED: "admin_scheduler_day_wed",
  THU: "admin_scheduler_day_thu",
  FRI: "admin_scheduler_day_fri",
  SAT: "admin_scheduler_day_sat",
};

const pad2 = (value: string | number): string => String(value).padStart(2, "0");

/** "*\/5" → 5, anything else → null */
const stepOf = (field: string): number | null => {
  const match = /^\*\/(\d+)$/.exec(field);
  return match ? Number(match[1]) : null;
};

/** True for a plain number or a comma-separated list of numbers. */
const isFixedList = (field: string): boolean => /^\d+(,\d+)*$/.test(field);

/** Render "3,15" with a fixed minute as "03:00, 15:00". */
const formatTimes = (hourField: string, minuteField: string): string => {
  const minutes = minuteField.split(",");
  const hours   = hourField.split(",");
  const out: string[] = [];
  hours.forEach(h => minutes.forEach(m => out.push(`${pad2(h)}:${pad2(m)}`)));
  return out.join(", ");
};

/** Render a day-of-week field as translated day names. */
const formatDays = (field: string, t: Translate): string =>
  field
    .split(",")
    .map(d => {
      const key = DAY_KEYS[d];
      return key ? t(key) : d;
    })
    .join(", ");

/**
 * Turn a cron expression into one plain sentence.
 *
 * Covers the shapes an operator actually produces from the dropdown or by
 * lightly editing it. Anything more exotic (ranges, #, L, mixed steps) falls
 * back to a generic line — the "next runs" preview beside it is the real answer
 * for those, and it comes from the server so it is always correct.
 */
export const describeCron = (cron: string, t: Translate): string => {
  const parts = normalizeCron(cron).split(" ").filter(Boolean);
  if (parts.length !== 6) {
    return t("admin_scheduler_desc_invalid");
  }

  const [sec, min, hour, dom, month, dow] = parts;

  const everyDay   = dom === "*" && month === "*" && (dow === "*" || dow === "?");
  const secStep    = stepOf(sec);
  const minStep    = stepOf(min);
  const hourStep   = stepOf(hour);

  // Sub-minute schedules
  if (sec === "*" && min === "*" && hour === "*" && everyDay) {
    return t("admin_scheduler_desc_every_second");
  }
  if (secStep !== null && min === "*" && hour === "*" && everyDay) {
    return t("admin_scheduler_desc_every_n_seconds", { n: secStep });
  }

  // Minute-level schedules (fixed second)
  if (isFixedList(sec) && hour === "*" && everyDay) {
    if (min === "*") {
      return t("admin_scheduler_desc_every_minute");
    }
    if (minStep !== null) {
      return t("admin_scheduler_desc_every_n_minutes", { n: minStep });
    }
    if (isFixedList(min)) {
      return t("admin_scheduler_desc_hourly_at", { minute: pad2(min.split(",")[0]) });
    }
  }

  // Hour-level schedules
  if (isFixedList(sec) && isFixedList(min) && hourStep !== null && everyDay) {
    return t("admin_scheduler_desc_every_n_hours", { n: hourStep, minute: pad2(min) });
  }

  if (isFixedList(sec) && isFixedList(min) && isFixedList(hour)) {
    const times = formatTimes(hour, min);

    // Weekly — a day-of-week restriction with no day-of-month restriction
    if (dom === "*" && month === "*" && dow !== "*" && dow !== "?") {
      return t("admin_scheduler_desc_weekly_at", { days: formatDays(dow, t), times });
    }
    // Monthly — a fixed day of the month
    if (isFixedList(dom) && month === "*") {
      return t("admin_scheduler_desc_monthly_at", { day: dom.split(",")[0], times });
    }
    // Daily
    if (everyDay) {
      return t("admin_scheduler_desc_daily_at", { times });
    }
  }

  return t("admin_scheduler_desc_custom");
};